import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getAppointmentByHashID, confirmAppointmentDepositByToken } from "../../services/Storeapi";
import PageShell from "../../components/PageShell/PageShell";
import {
  formatAppointmentWindow,
  formatCustomerName,
  formatStatusLabel,
  statusTone,
} from "../../utils/appointment";
import styles from "./confirmAppointment.module.css";

const TONE_CLASS = {
  success: styles.pillSuccess,
  warning: styles.pillWarning,
  danger: styles.pillDanger,
  neutral: styles.pillNeutral,
};

function StatusPill({ label, value }) {
  return (
    <span className={`${styles.pill} ${TONE_CLASS[statusTone(value)]}`}>
      <span className={styles.pillLabel}>{label}:</span>
      {formatStatusLabel(value)}
    </span>
  );
}

function ConfirmAppointment() {
  const navigate = useNavigate();
  const { hashedAppointmentID } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [appointment, setAppointment] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const fetchAppointment = useCallback(async () => {
    if (!hashedAppointmentID) {
      setLoadError("This confirmation link is missing an appointment reference.");
      setLoadState("error");
      return;
    }

    setLoadState("loading");
    setLoadError("");

    try {
      const payload = await getAppointmentByHashID(hashedAppointmentID, token);
      setAppointment(payload);
      setLoadState("ready");
    } catch (error) {
      console.error("fetch failed:", error);
      setLoadError(error?.message || "We couldn't load this appointment.");
      setLoadState("error");
    }
  }, [hashedAppointmentID, token]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  const handleConfirm = useCallback(async () => {
    if (submitting || !token) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      const payload = await confirmAppointmentDepositByToken(token, hashedAppointmentID);
      navigate("/payment-success", {
        state: { appointment, payload, status: "success" },
      });
    } catch (error) {
      console.error("confirm failed:", error);
      setSubmitError(error?.message || "Deposit confirmation failed.");
      navigate("/payment-failure", {
        state: {
          appointment,
          error: error?.message || "Deposit confirmation failed.",
          status: "failure",
        },
      });
    } finally {
      setSubmitting(false);
    }
  }, [appointment, hashedAppointmentID, navigate, submitting, token]);

  const appointmentWindow = formatAppointmentWindow(appointment?.start_at, appointment?.end_at);
  const skipsDeposit = appointment?.deposit_required_cents === 0;

  return (
    <PageShell>
      <div className={styles.container}>
        <section className={styles.card}>
          <header className={styles.cardHeader}>
            <h1 className={styles.title}>Confirm Appointment</h1>
            <p className={styles.subtitle}>
              {skipsDeposit ? "Review the details below and confirm without a deposit." : "Review the details below and confirm the appointment with any required deposit."}
            </p>
          </header>

          {!token && (
            <div className={`${styles.banner} ${styles.bannerWarning}`}>
              <p className={styles.bannerTitle}>Missing confirmation token</p>
              <p className={styles.bannerText}>
                Open this page from the original confirmation link to confirm the appointment.
              </p>
            </div>
          )}

          {loadState === "loading" && (
            <div className={styles.stateBlock}>
              <div className={styles.spinner} aria-hidden="true" />
              <p>Loading appointment…</p>
            </div>
          )}

          {loadState === "error" && (
            <div className={styles.stateBlock}>
              <div className={`${styles.banner} ${styles.bannerDanger}`}>
                <p className={styles.bannerTitle}>Unable to load this appointment</p>
                <p className={styles.bannerText}>{loadError}</p>
              </div>
              <button type="button" className={styles.secondaryButton} onClick={fetchAppointment}>
                Try again
              </button>
            </div>
          )}

          {loadState === "ready" && appointment && (
            <>
              <div className={styles.statusRow}>
                <StatusPill label="Status" value={appointment.status} />
                <StatusPill label="Payment" value={appointment.payment_status} />
              </div>

              <hr className={styles.divider} />

              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Appointment</h2>
                <dl className={styles.detailList}>
                  <dt className={styles.detailLabel}>Date</dt>
                  <dd className={styles.detailValue}>{appointmentWindow.date}</dd>

                  <dt className={styles.detailLabel}>Time</dt>
                  <dd className={styles.detailValue}>
                    {appointmentWindow.time}
                    {appointmentWindow.timeZone && (
                      <span className={styles.timeZone}>{appointmentWindow.timeZone}</span>
                    )}
                  </dd>
                </dl>
              </div>

              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Customer</h2>
                <dl className={styles.detailList}>
                  <dt className={styles.detailLabel}>Name</dt>
                  <dd className={styles.detailValue}>
                    {formatCustomerName(appointment.customer_info)}
                  </dd>

                  <dt className={styles.detailLabel}>Email</dt>
                  <dd className={styles.detailValue}>{appointment.customer_info?.email || "—"}</dd>

                  <dt className={styles.detailLabel}>Phone</dt>
                  <dd className={styles.detailValue}>{appointment.customer_info?.phone || "—"}</dd>
                </dl>
              </div>

              <section className={styles.section} aria-label="Customer questionnaire">
                <h2 className={styles.sectionTitle}>Customer questionnaire</h2>
                {appointment.customer_info?.questionnaire_answers?.length ? (
                  <dl className={styles.detailList}>
                    {appointment.customer_info.questionnaire_answers.map((answer, index) => (
                      <div className={styles.questionnaireAnswer} key={`${answer.question_id}-${index}`}>
                        <dt className={styles.detailLabel}>{answer.question || `Question ${answer.question_id}`}</dt>
                        <dd className={styles.detailValue}>{answer.answer?.trim() ? answer.answer : "No answer provided"}</dd>
                      </div>
                    ))}
                  </dl>
                ) : <p className={styles.hint}>No questionnaire answers on file.</p>}
              </section>

              {submitError && (
                <div className={`${styles.banner} ${styles.bannerDanger}`}>
                  <p className={styles.bannerTitle}>Confirmation needs attention</p>
                  <p className={styles.bannerText}>{submitError}</p>
                </div>
              )}

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleConfirm}
                  disabled={!token || submitting}
                >
                  {submitting && <span className={styles.buttonSpinner} aria-hidden="true" />}
                  {submitting ? "Processing…" : skipsDeposit ? "Confirm Appointment" : "Confirm & Charge Deposit"}
                </button>
                <p className={styles.hint}>{skipsDeposit ? "No deposit will be charged. The full balance is due after the appointment." : "Any required deposit is charged to the card on file at confirmation."}</p>
              </div>
            </>
          )}
        </section>
      </div>
    </PageShell>
  );
}

export default ConfirmAppointment;